import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone, objectDiff } from "@/lib/DataUtils";
import { createStore } from "../storeHelper";
import { findFirst } from "obj-traverse/lib/obj-traverse";
import * as itemUtils from "@/lib/ItemUtils";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:TemplatesStore`);

const DEFAULT_TEMPLATE_COLLECTION_EDITOR = {
  name: null,
  description: null,
};

const DEFAULT_CATEGORY_EDITOR = {
  name: null,
  information: null,
  printToPdfDefault: true,
};

const DEFAULT_CHECKLIST_EDITOR = {
  name: null,
  isCrossCheckList: false,
  isInformationOnly: false,
  printToPdfDefault: true,
};

const DEFAULT_CHECKLISTROW_EDITOR = {
  text: null,
  information: null,
  imageFileName: null,
};

const DEFAULT_EDITOR = {
  id: null,
  parentId: null,
  restrictionMode: "Mandatory",
  checklistRowsToImportText: "",
};

export const useTemplatesStore = defineStore(
  "templates",
  createStore({
    state: {
      catalog: {},
      isLoading: false,
      importWithParentId: null,
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      catalogList: (state) => _.values(state.catalog),
      templateItemById: (state) => (templateId) => {
        const values = _.values(state.catalog);
        let first;
        for (const v of values) {
          first = findFirst(v, "children", { id: templateId });
          if (first) {
            break;
          }
        }
        return first;
      },
      templateItemByTargetId: (state) => (targetId) => {
        const values = _.values(state.catalog);
        let first;
        for (const v of values) {
          first = findFirst(v, "children", { targetId: targetId });
          if (first) {
            break;
          }
        }
        return first;
      },
      editorChanges() {
        const itemId = _.get(this, "editor.id");
        if (!itemId) {
          return null;
        }
        const original = this.templateItemById(itemId);
        if (!original) {
          return null;
        }
        return objectDiff(original, this.editor);
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      addTemplateItem(templateItem) {
        this.catalog[templateItem.id] = templateItem;
      },

      clearCatalog() {
        this.catalog = {};
      },

      removeTemplateItem(templateId) {
        delete this.catalog[templateId];
      },

      newTemplateItemToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      newTemplateCollectionToTemplateItemEditor() {
        this.set(
          "editor.templateCollection",
          sureClone(DEFAULT_TEMPLATE_COLLECTION_EDITOR),
        );
      },

      newCategoryToTemplateItemEditor() {
        this.set("editor.category", sureClone(DEFAULT_CATEGORY_EDITOR));
      },

      newChecklistToTemplateItemEditor() {
        this.set("editor.checklist", sureClone(DEFAULT_CHECKLIST_EDITOR));
      },

      newChecklistRowToTemplateItemEditor() {
        this.set("editor.checklistRow", sureClone(DEFAULT_CHECKLISTROW_EDITOR));
      },

      async load() {
        debug("load");
        this.set("isLoading", true);
        try {
          const catalog = await ApiClient.get(`/templates`);
          debug("load", catalog);
          this.set("catalog", _.keyBy(catalog, "id"));
        } finally {
          this.set("isLoading", false);
        }
      },

      async loadRoot() {
        debug("loadRoot");
        this.set("isLoading", true);
        try {
          const catalog = await ApiClient.get(`/templates`);
          debug("loadRoot", catalog);
          this.set("catalog", _.keyBy(catalog, "id"));
        } finally {
          this.set("isLoading", false);
        }
      },

      async loadChildren(templateItemId) {
        debug("loadChildren", templateItemId);
        const children = await ApiClient.get(
          `/templates/${templateItemId}/children`,
        );
        debug("loadChildren result", children);

        const parent = this.templateItemById(templateItemId);
        if (parent) {
          parent.children = children;
        }
      },

      async moveUpTemplateItem(templateItemId) {
        debug("moveUpTemplateItem", templateItemId);
        const result = await ApiClient.post(
          `/templateItems/${templateItemId}/MoveUp`,
          templateItemId,
        );
        debug("moveUpTemplateItem, result", result);
      },

      async moveDownTemplateItem(templateItemId) {
        debug("moveDownTemplateItem", templateItemId);
        const result = await ApiClient.post(
          `/templateItems/${templateItemId}/MoveDown`,
          templateItemId,
        );
        debug("moveDownTemplateItem, result", result);
      },

      async createTemplateItem() {
        const templateItem = sureClone(this.editor);
        _.unset(templateItem, "id");
        debug("createTemplateItem", templateItem);
        const response = await ApiClient.post("/templateItems", templateItem);
        debug("createTemplateItem - response", response);

        // Handle cross-store commits for different entity types
        if (response.category != null) {
          const { useCategoriesStore } = await import("../categories");
          const categoriesStore = useCategoriesStore();
          categoriesStore.addCategory(response.category);
        }

        if (response.checklist != null) {
          const { useChecklistsStore } = await import("../checklists");
          const checklistsStore = useChecklistsStore();
          checklistsStore.addChecklist(response.checklist);
        }

        if (response.checklistRow != null) {
          const { useChecklistRowsStore } = await import("../checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          checklistRowsStore.addChecklistRow(response.checklistRow);
        }

        return response;
      },

      async loadTemplateItemToEditor(templateItemId) {
        debug("loadTemplateItemToEditor", templateItemId);
        const templateItem = this.templateItemById(templateItemId);
        if (!templateItem) {
          throw new Error("Template item not found");
        }

        this.set("editor", sureClone(templateItem));

        // Load related entities based on type
        if (templateItem.type === "CATEGORY") {
          const { useCategoriesStore } = await import("../categories");
          const categoriesStore = useCategoriesStore();
          const category = categoriesStore.categoryById(templateItem.targetId);
          this.set("editor.category", sureClone(category));
        }

        if (templateItem.type === "CHECKLIST") {
          const { useChecklistsStore } = await import("../checklists");
          const checklistsStore = useChecklistsStore();
          const checklist = checklistsStore.checklistById(
            templateItem.targetId,
          );
          this.set("editor.checklist", sureClone(checklist));
        }

        if (templateItem.type === "CHECKLISTROW") {
          const { useChecklistRowsStore } = await import("../checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          const checklistRow = checklistRowsStore.checklistRowById(
            templateItem.targetId,
          );
          this.set("editor.checklistRow", sureClone(checklistRow));
        }
      },

      async copyTemplateItem({ templateItemId, targetTemplateItemId }) {
        debug("copyTemplateItem", templateItemId);
        const response = await ApiClient.post(
          `/templateItems/${templateItemId}/clone?TargetId=${targetTemplateItemId}`,
        );
        debug("copyTemplateItem - response", response);
        this.addTemplateItem(response);
      },

      async saveTemplateItemInEditor() {
        if (!this.editorHasChanges) {
          debug("saveTemplateItemInEditor", "No changes to save");
          return;
        }

        const templateItemId = this.editor.id;
        const result = await ApiClient.put(
          `/templateItems/${templateItemId}`,
          this.editor,
        );

        if (result) {
          this.addTemplateItem(result);
        }
        return result;
      },

      async syncChangesInTarget(data) {
        const templateItem = this.templateItemByTargetId(data.id);
        if (templateItem) {
          templateItem.name = data.name;
          templateItem.restrictionMode = itemUtils.getRestrictionModeByNumber(
            data.restrictionModeNumber,
          );
        }
      },

      async deleteTemplateItem(templateItemId) {
        debug("deleteTemplateItem", templateItemId);
        const result = await ApiClient.delete(
          `/templateItems/${templateItemId}`,
        );
        debug("deleteTemplateItem - result", result);
        this.removeTemplateItem(templateItemId);

        // Handle cascading deletes
        if (result?.categoryId != null) {
          const { useCategoriesStore } = await import("../categories");
          const categoriesStore = useCategoriesStore();
          categoriesStore.deleteCategory(result.categoryId);
        }
        if (result?.checklistId != null) {
          const { useChecklistsStore } = await import("../checklists");
          const checklistsStore = useChecklistsStore();
          checklistsStore.deleteChecklist(result.checklistId);
        }
        if (result?.checklistRowId != null) {
          const { useChecklistRowsStore } = await import("../checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          checklistRowsStore.deleteChecklistRow(result.checklistRowId);
        }
      },

      async deleteTemplateItemById(templateItemId) {
        return await this.deleteTemplateItem(templateItemId);
      },
    },
  }),
);
